import { useCallback, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';

import { Button } from '@/components/ui/button';
import { useAccount } from '@/hooks/useAccount';
import { cn } from '@/lib/utils';
import { type DateRange, selectionToPeriod } from '@/modules/analytics/dateSelection';
import { useDateFilter } from '@/modules/analytics/hooks/useDateFilter';
import { type Period } from '@/modules/analytics/types';
import { getPeriodDates } from '@/modules/analytics/utils';
import { formatDateRangeLabel } from '@/modules/analytics/utils/formatDateRangeLabel';

import { DateRangeMenu } from './DateRangeMenu';
import { useRetentionBounds } from './useRetentionBounds';

interface Props {
  dateOpen: boolean;
  setDateOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  disabled: boolean;
  className?: string;
  align?: 'start' | 'end';
  onDateApplied?: () => void;
}

const DateFilter = ({
  dateOpen,
  setDateOpen,
  disabled,
  className,
  align = 'start',
  onDateApplied,
}: Props) => {
  const { t } = useTranslation();

  const { startDate, endDate, selection, handlePeriodClick, applyRange } =
    useDateFilter(setDateOpen);

  const selectedPeriod = useMemo(() => selectionToPeriod(selection), [selection]);

  const { account } = useAccount();
  const logsLimit = account?.max_log_retention;

  const menuRef = useRef(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useClickAway(menuRef, (event) => {
    if (calendarRef.current?.contains(event.target as Node)) {
      return;
    }
    setDateOpen(false);
  });

  const { isPresetDisabled, isDayDisabled, earliest, today } = useRetentionBounds(logsLimit);

  const handlePresetSelect = useCallback(
    (period: Period) => {
      const resolved = getPeriodDates(startDate, endDate, period);
      const isReselect =
        resolved.startDate === startDate &&
        resolved.endDate === endDate &&
        selectedPeriod?.title_key === period.title_key;
      handlePeriodClick(period);
      if (!isReselect) {
        onDateApplied?.();
      }
    },
    [endDate, handlePeriodClick, onDateApplied, selectedPeriod, startDate]
  );

  const handleRangeSelect = useCallback(
    (range: DateRange) => {
      applyRange(range);
      onDateApplied?.();
    },
    [applyRange, onDateApplied]
  );

  const triggerLabel = (() => {
    if (startDate && endDate) {
      return formatDateRangeLabel(startDate, endDate);
    }
    const single = startDate || endDate;
    return single ? formatDateRangeLabel(single, single) : t('analytics.periods.custom');
  })();

  return (
    <div className={cn('relative z-30', className)} ref={menuRef}>
      <Button
        variant="outline-neutral"
        size="sm"
        className="leading-6"
        disabled={disabled}
        aria-expanded={dateOpen}
        aria-haspopup="dialog"
        onClick={() => setDateOpen((prev) => !prev)}
      >
        {triggerLabel}
      </Button>

      {dateOpen ? (
        <DateRangeMenu
          ref={calendarRef}
          startDate={startDate}
          endDate={endDate}
          onPresetSelect={handlePresetSelect}
          onRangeSelect={handleRangeSelect}
          isPresetDisabled={isPresetDisabled}
          isDayDisabled={isDayDisabled}
          retentionStart={earliest}
          today={today}
          selectedPeriod={selectedPeriod}
          align={align}
          onRequestClose={() => setDateOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default DateFilter;
